/**
 * Social marquee: repeat tile sets to fill the viewport (seamless -50% loop),
 * pause animation and videos when off-screen, play videos when visible.
 */
(function () {
  const videoOptions = {
    root: null,
    rootMargin: '0px 0px -10% 0px',
    threshold: [0, 0.15, 0.35],
  };

  const sectionOptions = {
    root: null,
    rootMargin: '12% 0px 12% 0px',
    threshold: 0,
  };

  const MAX_SET_COPIES = 32;

  function debounce(fn, ms) {
    let t;
    return function debounced() {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  /**
   * Clean cloned sets: Shopify editor attrs (duplicate ids confuse the editor) and
   * our `data-marquee-*` markers (cloneNode copies them so videos would skip video init).
   */
  function sanitizeEditorClone(rootEl) {
    const nodes = [rootEl, ...rootEl.querySelectorAll('*')];
    nodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const id = node.getAttribute('id');
      if (id && id.indexOf('shopify-block') === 0) node.removeAttribute('id');
      const attrs = node.attributes ? Array.from(node.attributes) : [];
      attrs.forEach((attr) => {
        if (attr.name.indexOf('data-shopify-') === 0) node.removeAttribute(attr.name);
        if (attr.name.indexOf('data-marquee-') === 0) node.removeAttribute(attr.name);
      });
    });
  }

  function viewportWidth() {
    return (
      document.documentElement.clientWidth ||
      window.innerWidth ||
      0
    );
  }

  /**
   * One "set" is the full tile sequence from Liquid. Clone it an even number of times
   * so total width >= ~2× viewport (smooth infinite scroll with translateX(-50%)).
   */
  function ensureMarqueeSets(root) {
    const track = root.querySelector('[data-marquee-track]');
    const first = track && track.querySelector('[data-marquee-set]');
    if (!track || !first) return;

    track.querySelectorAll('.social-marquee__set').forEach((el, index) => {
      if (index > 0) el.remove();
    });

    const setWidth = first.getBoundingClientRect().width;
    if (!setWidth || setWidth < 1) return;

    const vw = viewportWidth() || root.getBoundingClientRect().width || setWidth * 4;

    let copies = Math.ceil((vw * 2) / setWidth);
    copies = Math.min(copies, MAX_SET_COPIES);
    copies = Math.max(copies, 2);
    if (copies % 2 !== 0) copies += 1;

    for (let i = 1; i < copies; i += 1) {
      const clone = first.cloneNode(true);
      sanitizeEditorClone(clone);
      track.appendChild(clone);
    }
  }

  function initMarqueeVideos(root) {
    root.querySelectorAll('video').forEach((video) => {
      if (video.dataset.marqueeVideoIo === '1') return;
      video.dataset.marqueeVideoIo = '1';

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('muted', '');

      const videoIo = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const ratio = entry.intersectionRatio;
          const visibleEnough = entry.isIntersecting && ratio >= 0.2;
          if (visibleEnough) {
            video.play().catch(() => {});
          } else {
            video.pause();
            try {
              video.currentTime = 0;
            } catch (_) {}
          }
        });
      }, videoOptions);

      videoIo.observe(video);
    });
  }

  function attachSectionPause(root, track) {
    if (!track || root.dataset.marqueeSectionIo === '1') return;
    root.dataset.marqueeSectionIo = '1';

    const sectionIo = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          track.classList.remove('is-paused');
        } else {
          track.classList.add('is-paused');
        }
      });
    }, sectionOptions);

    sectionIo.observe(root);
  }

  function refreshMarqueeLayout(root) {
    ensureMarqueeSets(root);
    initMarqueeVideos(root);
  }

  function bindMarquee(root) {
    if (!root || root.dataset.marqueeBound === 'true') return;
    root.dataset.marqueeBound = 'true';

    const track = root.querySelector('[data-marquee-track]');
    attachSectionPause(root, track);

    const runLayout = () => refreshMarqueeLayout(root);
    runLayout();

    const firstSet = root.querySelector('[data-marquee-set]');
    const debounced = debounce(runLayout, 150);
    window.addEventListener('resize', debounced);

    if (firstSet && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(debounced);
      ro.observe(firstSet);
    }

    let attempts = 0;
    function retryUntilSized() {
      const set = root.querySelector('[data-marquee-set]');
      const w = set && set.getBoundingClientRect().width;
      if (w && w >= 1) {
        refreshMarqueeLayout(root);
        return;
      }
      attempts += 1;
      if (attempts < 40) requestAnimationFrame(retryUntilSized);
    }
    requestAnimationFrame(retryUntilSized);
  }

  document.querySelectorAll('[data-social-marquee]').forEach(bindMarquee);

  document.addEventListener('shopify:section:load', (event) => {
    const inner =
      event.target && event.target.querySelector && event.target.querySelector('[data-social-marquee]');
    if (inner) bindMarquee(inner);
  });
})();
